import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Button from '../components/atoms/Button';
import Icon from '../components/atoms/Icon';
import { API_BASE_URL } from '../config';
import { useAuth } from '../hooks/useAuth';

const VerificationWelcomePage = () => {
  const navigate = useNavigate();
  const { currentSession, isLoggedIn, profile, isLoading: authLoading } = useAuth();
  const [loading, setLoading] = useState(true);
  const [config, setConfig] = useState(null);

  useEffect(() => {
    if (authLoading) return;
    const hasSession = isLoggedIn() || localStorage.getItem('dutlaunch_current_session');
    if (!hasSession) {
      navigate('/login');
      return;
    }
    fetchConfig();
  }, [currentSession, isLoggedIn, profile, authLoading, navigate]);

  const fetchConfig = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/verification/config`);
      const data = await response.json();
      setConfig(data);
    } catch (error) {
      console.error('Error fetching config:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleStartVerification = () => {
    let candidateId = profile?.id || currentSession?.user_id;
    let candidateEmail = profile?.email || currentSession?.email;
    let candidateName = profile?.full_name ||
      `${profile?.first_name || ''} ${profile?.last_name || ''}`.trim() ||
      profile?.username ||
      candidateEmail;

    if (!candidateId || !candidateEmail) {
      const currentRole = localStorage.getItem('dutlaunch_current_session');
      if (currentRole) {
        const profileStr = localStorage.getItem(`dutlaunch_${currentRole}_profile`);
        candidateId = candidateId || localStorage.getItem(`dutlaunch_${currentRole}_user_id`);
        candidateEmail = candidateEmail || localStorage.getItem(`dutlaunch_${currentRole}_email`);
        if (profileStr) {
          try {
            const profileData = JSON.parse(profileStr);
            candidateName = profileData.full_name ||
              `${profileData.first_name || ''} ${profileData.last_name || ''}`.trim() ||
              profileData.username || candidateEmail;
          } catch (e) { /* ignore */ }
        }
      }
    }

    if (!candidateId || !candidateEmail) {
      alert('Please log in again to continue');
      navigate('/login');
      return;
    }

    navigate('/verification-assessment', {
      state: { candidateId, candidateEmail, candidateName }
    });
  };

  const hasSessionData = currentSession || localStorage.getItem('dutlaunch_current_session');

  if (loading || authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-teal-600"></div>
      </div>
    );
  }

  if (!loading && !authLoading && !hasSessionData) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
        <div className="max-w-sm w-full bg-white rounded-xl shadow-sm border border-gray-200 p-6 text-center">
          <Icon name="error" size={48} className="text-red-500 mx-auto mb-3" />
          <h2 className="text-lg font-bold text-gray-900 mb-2">Session Expired</h2>
          <p className="text-sm text-gray-600 mb-4">Please log in again to continue.</p>
          <Button size="md" onClick={() => navigate('/login')} className="w-full">Go to Login</Button>
        </div>
      </div>
    );
  }

  const totalQuestions = config
    ? config.voice_questions_count + config.mcq_questions_count + config.situational_questions_count
    : 20;

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="text-center mb-6">
          <div className="inline-flex items-center justify-center w-14 h-14 bg-teal-600 rounded-full mb-3">
            <Icon name="verified_user" size={28} className="text-white" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900">Profile Verification</h1>
          <p className="text-sm text-gray-600 mt-1">
            Get verified to boost your visibility by 3x
          </p>
        </div>

        {/* Main Card */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          {/* Assessment Structure */}
          <div className="p-5 border-b border-gray-100">
            <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wide mb-3">Assessment Structure</h3>
            <div className="grid grid-cols-3 gap-3">
              <div className="bg-teal-50 rounded-lg p-3 text-center">
                <Icon name="mic" size={20} className="text-teal-600 mx-auto mb-1" />
                <p className="text-xl font-bold text-teal-700">{config?.voice_questions_count || 12}</p>
                <p className="text-xs text-gray-600">Voice</p>
              </div>
              <div className="bg-teal-50 rounded-lg p-3 text-center">
                <Icon name="quiz" size={20} className="text-teal-600 mx-auto mb-1" />
                <p className="text-xl font-bold text-teal-700">{config?.mcq_questions_count || 5}</p>
                <p className="text-xs text-gray-600">MCQ</p>
              </div>
              <div className="bg-teal-50 rounded-lg p-3 text-center">
                <Icon name="psychology" size={20} className="text-teal-600 mx-auto mb-1" />
                <p className="text-xl font-bold text-teal-700">{config?.situational_questions_count || 3}</p>
                <p className="text-xs text-gray-600">Situational</p>
              </div>
            </div>
            <p className="text-center text-xs text-gray-500 mt-2">
              {totalQuestions} questions &bull; ~20-25 min
            </p>
          </div>

          {/* Proctoring + Requirements - side by side */}
          <div className="grid grid-cols-2 divide-x divide-gray-100">
            <div className="p-4">
              <h3 className="text-xs font-semibold text-gray-900 uppercase tracking-wide mb-2 flex items-center gap-1">
                <Icon name="videocam" size={14} className="text-amber-500" />
                Monitoring
              </h3>
              <ul className="space-y-1.5 text-xs text-gray-600">
                <li className="flex items-center gap-1.5">
                  <Icon name="face" size={12} className="text-gray-400" /> Face detection
                </li>
                <li className="flex items-center gap-1.5">
                  <Icon name="visibility" size={12} className="text-gray-400" /> Eye tracking
                </li>
                <li className="flex items-center gap-1.5">
                  <Icon name="devices" size={12} className="text-gray-400" /> Device detection
                </li>
                <li className="flex items-center gap-1.5">
                  <Icon name="people" size={12} className="text-gray-400" /> Multi-face detection
                </li>
              </ul>
            </div>
            <div className="p-4">
              <h3 className="text-xs font-semibold text-gray-900 uppercase tracking-wide mb-2 flex items-center gap-1">
                <Icon name="checklist" size={14} className="text-teal-500" />
                Requirements
              </h3>
              <ul className="space-y-1.5 text-xs text-gray-600">
                <li className="flex items-center gap-1.5">
                  <Icon name="check" size={12} className="text-teal-500" /> Good lighting
                </li>
                <li className="flex items-center gap-1.5">
                  <Icon name="check" size={12} className="text-teal-500" /> Quiet environment
                </li>
                <li className="flex items-center gap-1.5">
                  <Icon name="check" size={12} className="text-teal-500" /> Stable internet
                </li>
                <li className="flex items-center gap-1.5">
                  <Icon name="check" size={12} className="text-teal-500" /> Camera & mic access
                </li>
              </ul>
            </div>
          </div>

          {/* Why Verify - compact */}
          <div className="px-5 py-3 bg-teal-50 border-t border-teal-100">
            <div className="flex items-center justify-between text-xs">
              <span className="flex items-center gap-1.5 text-teal-700">
                <Icon name="trending_up" size={14} /> 3x more visibility
              </span>
              <span className="flex items-center gap-1.5 text-teal-700">
                <Icon name="verified" size={14} /> Trust badge on profile
              </span>
              <span className="flex items-center gap-1.5 text-teal-700">
                <Icon name="insights" size={14} /> Showcase your skills
              </span>
            </div>
          </div>

          {/* Important Notes */}
          <div className="px-5 py-3 border-t border-gray-100 bg-gray-50">
            <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-500">
              <span>One-time assessment</span>
              <span>&bull;</span>
              <span>Personalized questions</span>
              <span>&bull;</span>
              <span>Scores are private</span>
              <span>&bull;</span>
              <span>Warnings won't fail you</span>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="p-4 flex gap-3">
            <button
              onClick={() => navigate('/candidate/dashboard')}
              className="flex-1 px-4 py-2.5 text-sm text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Maybe Later
            </button>
            <button
              onClick={handleStartVerification}
              disabled={!hasSessionData}
              className="flex-1 px-4 py-2.5 text-sm font-semibold text-white bg-teal-600 rounded-lg hover:bg-teal-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-1.5"
            >
              <Icon name="play_arrow" size={18} />
              Start Verification
            </button>
          </div>
        </div>

        <p className="text-center text-xs text-gray-400 mt-4">
          By starting, you agree to our monitoring and assessment policies.
        </p>
      </div>
    </div>
  );
};

export default VerificationWelcomePage;
